import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CredentialsPopup, { type CredentialsNextLinks } from '../base/CredentialsPopup';
import { buildProvisionCredentials } from '../base/provisionCredentials';

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  createColumnHelper,
  flexRender,
  type SortingState,
} from '@tanstack/react-table';
import StatusBadge, { PlanBadge, ActionIconButton } from '../../../components/base/StatusBadge';
import OrderPlanDetailModal from './OrderPlanDetailModal';
import {
  fetchOrderPlansList,
  fetchOrderPlansTotal,
} from '../../../api/orderPlans.api';
import type { AdminOrderPlan, OrderPlanStatusValue } from '../../../types/orderPlan.types';
import { formatPlanMoney } from '../../../lib/planCurrency';

const PER_PAGE = 15;

type FilterType = 'all' | 'awaiting_review' | OrderPlanStatusValue;
const columnHelper = createColumnHelper<AdminOrderPlan>();

export default function OrderPlansTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState<AdminOrderPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [globalFilter, setGlobalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<FilterType>('awaiting_review');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [detailId, setDetailId] = useState<number | null>(null);

  const [summary, setSummary] = useState({
    total: 0,
    awaiting: 0,
    approved: 0,
    rejected: 0,
  });

  const [credentialsPopup, setCredentialsPopup] = useState<{
    isOpen: boolean;
    email: string;
    password: string;
    tenantName?: string;
    loginUrl?: string;
    nextLinks?: CredentialsNextLinks;
  }>({
    isOpen: false,
    email: '',
    password: '',
    tenantName: '',
    loginUrl: '',
  });

  const loadSummary = useCallback(async () => {
    const [allR, awaitingR, pendingR, approvedR, rejectedR] = await Promise.all([
      fetchOrderPlansTotal(),
      fetchOrderPlansTotal('payment_submitted'),
      fetchOrderPlansTotal('pending'),
      fetchOrderPlansTotal('approved'),
      fetchOrderPlansTotal('rejected'),
    ]);
    if (
      allR.ok === false ||
      awaitingR.ok === false ||
      pendingR.ok === false ||
      approvedR.ok === false ||
      rejectedR.ok === false
    ) {
      return;
    }
    setSummary({
      total: allR.total,
      awaiting: awaitingR.total + pendingR.total,
      approved: approvedR.total,
      rejected: rejectedR.total,
    });
  }, []);

  const loadList = useCallback(async () => {
    setListError('');
    setLoading(true);
    try {
      if (statusFilter === 'awaiting_review') {
        // Merge pending + payment_submitted for the review queue
        const [submitted, pending] = await Promise.all([
          fetchOrderPlansList(page, PER_PAGE, { status: 'payment_submitted' }),
          fetchOrderPlansList(1, PER_PAGE, { status: 'pending' }),
        ]);
        setLoading(false);
        if (submitted.ok === false) {
          if (submitted.unauthorized) {
            navigate('/admin/login', { replace: true });
            return;
          }
          setListError(submitted.message);
          setRows([]);
          return;
        }
        if (pending.ok === false) {
          setListError(pending.message);
          setRows([]);
          return;
        }
        const merged = [...(submitted.list.data ?? []), ...(pending.list.data ?? [])].sort(
          (a, b) => b.id - a.id,
        );
        // Simple client page over merged first page sets — good enough for staging queue size
        const start = (page - 1) * PER_PAGE;
        const pageRows = merged.slice(start, start + PER_PAGE);
        const mergedTotal = submitted.list.total + pending.list.total;
        setRows(pageRows);
        setTotal(mergedTotal);
        setLastPage(Math.max(1, Math.ceil(mergedTotal / PER_PAGE)));
      } else {
        const r = await fetchOrderPlansList(page, PER_PAGE, {
          status: statusFilter === 'all' ? undefined : statusFilter,
        });
        setLoading(false);
        if (r.ok === false) {
          if (r.unauthorized) {
            navigate('/admin/login', { replace: true });
            return;
          }
          setListError(r.message);
          setRows([]);
          return;
        }
        setRows(r.list.data ?? []);
        setLastPage(Math.max(1, r.list.last_page));
        setTotal(r.list.total);
      }
    } catch (err) {
      setLoading(false);
      setListError(err instanceof Error ? err.message : 'Failed to load data');
      setRows([]);
    }
  }, [navigate, page, statusFilter]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  const filteredRows = useMemo(() => {
    const q = globalFilter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        String(r.id).includes(q) ||
        String(r.name ?? '').toLowerCase().includes(q) ||
        String(r.email ?? '').toLowerCase().includes(q) ||
        String(r.phone ?? '').toLowerCase().includes(q) ||
        String(r.tenant_id ?? '').toLowerCase().includes(q) ||
        String(r.plan?.title ?? '').toLowerCase().includes(q),
    );
  }, [rows, globalFilter]);

  const openCredentials = useCallback(
    (payload: Parameters<typeof buildProvisionCredentials>[0], fallbackEmail?: string) => {
      const creds = buildProvisionCredentials(payload, fallbackEmail);
      if (!creds) return;
      setCredentialsPopup({
        isOpen: true,
        email: creds.email,
        password: creds.password,
        tenantName: creds.tenantName,
        loginUrl: creds.loginUrl,
        nextLinks: creds.nextLinks,
      });
      void loadSummary();
    },
    [loadSummary],
  );

  const handleCredentialsPopupClose = useCallback(() => {
    setCredentialsPopup((prev) => ({ ...prev, isOpen: false }));
    void loadList();
    void loadSummary();
  }, [loadList, loadSummary]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: () => t('order_plans.col.id'),
        cell: (info) => (
          <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-1 rounded">
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor((row) => row.name ?? '-', {
        id: 'name',
        header: () => t('order_plans.col.name'),
        cell: (info) => (
          <div>
            <p className="text-sm font-semibold text-gray-900">{info.getValue()}</p>
            <p className="text-xs text-gray-400 truncate max-w-[200px]">
              {info.row.original.email ?? '-'}
            </p>
          </div>
        ),
      }),
      columnHelper.accessor((row) => row.phone ?? '-', {
        id: 'phone',
        header: () => t('order_plans.col.phone'),
        cell: (info) => <span className="text-sm text-gray-700 font-mono">{info.getValue()}</span>,
      }),
      columnHelper.accessor((row) => row.plan?.title ?? '', {
        id: 'planTitle',
        header: () => t('order_plans.col.plan'),
        cell: (info) => <PlanBadge plan={info.getValue()} />,
      }),
      columnHelper.accessor((row) => row.plan?.price ?? '', {
        id: 'planPrice',
        header: () => t('order_plans.col.price'),
        cell: (info) => {
          const row = info.row.original;
          const price = row.plan?.price;
          const days = row.plan?.days;
          return (
            <div>
              <p className="text-sm font-bold text-gray-900">
                {price ? formatPlanMoney(price, row.plan?.currency) : '-'}
              </p>
              <p className="text-xs text-gray-400">
                {days ? `${days} ${t('plans.duration_days')}` : '-'}
              </p>
            </div>
          );
        },
      }),
      columnHelper.accessor((row) => row.tenant_id ?? '-', {
        id: 'tenant_id',
        header: () => t('order_plans.col.tenant_id'),
        cell: (info) => (
          <span className="text-xs font-mono text-gray-600 break-all max-w-[140px] inline-block">
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor((row) => row.subscription_id ?? '-', {
        id: 'subscription_id',
        header: () => t('order_plans.col.subscription_id'),
        cell: (info) => <span className="text-sm font-mono text-gray-700">{info.getValue()}</span>,
      }),
      columnHelper.accessor((row) => row.status ?? 'pending', {
        id: 'status',
        header: () => t('order_plans.col.status'),
        cell: (info) => <StatusBadge status={info.getValue()} />,
      }),
      columnHelper.accessor((row) => row.created_at ?? '-', {
        id: 'created_at',
        header: () => t('order_plans.col.created_at'),
        cell: (info) => (
          <div className="flex items-center gap-1.5 text-sm text-gray-600">
            <i className="ri-calendar-line text-gray-400 text-xs" />
            {info.getValue()}
          </div>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: () => t('order_plans.col.actions'),
        cell: (info) => (
          <div className="flex items-center gap-1">
            <ActionIconButton
              icon="ri-eye-line"
              label={t('actions.view_details')}
              onClick={() => setDetailId(info.row.original.id)}
            />
          </div>
        ),
      }),
    ],
    [t],
  );

  const table = useReactTable({
    data: filteredRows,
    columns,
    state: { globalFilter, sorting },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const filters: { value: FilterType; labelKey: string }[] = [
    { value: 'awaiting_review', labelKey: 'order_plans.filter.awaiting_review' },
    { value: 'all', labelKey: 'order_plans.filter.all' },
    { value: 'approved', labelKey: 'order_plans.filter.approved' },
    { value: 'rejected', labelKey: 'order_plans.filter.rejected' },
  ];

  const hasSearch = globalFilter.trim().length > 0;
  const displayFrom = rows.length === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const displayTo = rows.length === 0 ? 0 : (page - 1) * PER_PAGE + rows.length;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <button
          type="button"
          onClick={() => {
            setStatusFilter('awaiting_review');
            setPage(1);
          }}
          className={`text-start bg-white rounded-xl p-4 ring-1 transition-shadow cursor-pointer ${
            statusFilter === 'awaiting_review'
              ? 'ring-orange-300 shadow-sm'
              : 'ring-gray-100 hover:ring-orange-200'
          }`}
        >
          <p className="text-xs text-orange-700 font-medium">{t('order_plans.summary.awaiting')}</p>
          <p className="text-2xl font-bold text-orange-600 mt-1">{loading ? '—' : summary.awaiting}</p>
        </button>
        <div className="bg-white rounded-xl p-4 ring-1 ring-gray-100">
          <p className="text-xs text-gray-500 font-medium">{t('order_plans.summary.total')}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{loading ? '—' : summary.total}</p>
        </div>
        <div className="bg-white rounded-xl p-4 ring-1 ring-gray-100">
          <p className="text-xs text-gray-500 font-medium">{t('order_plans.summary.approved')}</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1">{loading ? '—' : summary.approved}</p>
        </div>
        <div className="bg-white rounded-xl p-4 ring-1 ring-gray-100">
          <p className="text-xs text-gray-500 font-medium">{t('order_plans.summary.rejected')}</p>
          <p className="text-2xl font-bold text-rose-600 mt-1">{loading ? '—' : summary.rejected}</p>
        </div>
      </div>

      {listError ? (
        <div className="rounded-xl bg-rose-50 text-rose-800 text-sm px-4 py-3 ring-1 ring-rose-100">
          {listError}
        </div>
      ) : null}

      <div className="bg-white rounded-xl ring-1 ring-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {filters.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => {
                  setStatusFilter(f.value);
                  setPage(1);
                }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  statusFilter === f.value
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t(f.labelKey)}
                {f.value === 'awaiting_review' && summary.awaiting > 0 ? (
                  <span className="ms-1.5 inline-flex min-w-[1.25rem] justify-center rounded-full bg-white/20 px-1">
                    {summary.awaiting}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
          <div className="relative flex-shrink-0">
            <div className="w-4 h-4 flex items-center justify-center absolute start-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              <i className="ri-search-line text-xs" />
            </div>
            <input
              type="text"
              placeholder={t('order_plans.search_placeholder')}
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="w-52 ps-8 pe-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all"
            />
          </div>
        </div>

        <p className="px-5 py-2 text-xs text-gray-500 bg-gray-50/80 border-b border-gray-50">
          {t('order_plans.detail.review_first')}
        </p>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-500">
              <i className="ri-loader-4-line text-3xl text-teal-600 animate-spin" />
              <p className="text-sm">{t('order_plans.loading')}</p>
            </div>
          ) : (
            <table className="w-full min-w-[960px]">
              <thead className="bg-gray-50/80">
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-4 py-3 text-start text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer whitespace-nowrap"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-1">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getIsSorted() && (
                            <i
                              className={`ri-arrow-${header.column.getIsSorted() === 'asc' ? 'up' : 'down'}-s-line text-teal-500`}
                            />
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-50">
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-4 py-16 text-center">
                      <div className="flex flex-col items-center gap-3 text-gray-400">
                        <i className="ri-shopping-bag-3-line text-4xl" />
                        <p className="text-sm font-medium">{t('table.no_order_plans')}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className="hover:bg-gray-50/60 transition-colors cursor-pointer"
                      onClick={() => setDetailId(row.original.id)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-4 py-3 align-top">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-50 flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm text-gray-500">
            {hasSearch
              ? t('order_plans.search_on_page', { count: filteredRows.length })
              : `${t('table.showing')} ${displayFrom}–${displayTo} ${t('table.of')} ${total} ${t('table.order_plans_count')}`}
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
            <span className="text-sm text-gray-600 px-2 min-w-[4rem] text-center">
              {page} / {lastPage}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= lastPage || loading}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
          </div>
        </div>
      </div>

      <OrderPlanDetailModal
        orderPlanId={detailId}
        onClose={() => setDetailId(null)}
        onUpdated={() => {
          void loadList();
          void loadSummary();
        }}
        onShowCredentials={(payload, fallbackEmail) => {
          openCredentials(payload, fallbackEmail);
        }}
      />

      <CredentialsPopup
        isOpen={credentialsPopup.isOpen}
        onClose={handleCredentialsPopupClose}
        email={credentialsPopup.email}
        password={credentialsPopup.password}
        tenantName={credentialsPopup.tenantName}
        loginUrl={credentialsPopup.loginUrl}
        nextLinks={credentialsPopup.nextLinks}
      />
    </div>
  );
}
